import {AdminApiClient} from './AdminApiClient';
import {IHttpTransport, _RequestEnvelope} from './core/types';
import {Session, SessionInfo, TokenInfo} from './misc/session';
import {me, refreshAuthToken} from './methods/auth';

export class SessionExpiredError extends Error {
    constructor() {
        super('Session has expired or been invalidated');
        this.name = 'SessionExpiredError';
    }
}

function parseJwtExpiry(token: string): Date {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return new Date(payload.exp * 1000);
}

export type SetSessionOptions = {
    refreshBeforeExpireMinutes?: number;
    onTokensRefreshed?: (sessionInfo: SessionInfo) => void;
    onSessionClosed?: () => void;
};

export class SessionAdminApiClient {
    private readonly _client: AdminApiClient;
    private _session: Session | null = null;
    private _sessionWasSet: boolean = false;

    constructor(baseUrl: string, transport?: IHttpTransport) {
        this._client = new AdminApiClient(baseUrl, transport);
    }

    async setSession(
        authToken: string,
        refreshToken: string,
        options?: SetSessionOptions
    ): Promise<void> {
        const expiresAt = parseJwtExpiry(authToken);
        const refreshBeforeMs = (options?.refreshBeforeExpireMinutes ?? 5) * 60 * 1000;
        const refreshAt = expiresAt.getTime() - refreshBeforeMs;

        const meResponse = await this._client.call(me(), authToken);
        const userId = meResponse.Admin.Id;

        const tokenInfo: TokenInfo = {expiresAt, refreshAt};

        const sessionInfo: SessionInfo = {
            userId,
            authToken,
            refreshToken,
        };

        const session = Session.create(sessionInfo, tokenInfo, {
            tokenRefresher: async (rt, at) => {
                const response = await this._client.call(
                    refreshAuthToken({RefreshToken: rt}),
                    at
                );
                if (response.Result !== 'Ok') return null;
                const newExpiresAt = parseJwtExpiry(response.AuthToken);
                return {
                    authToken: response.AuthToken,
                    refreshToken: response.RefreshToken,
                    tokenInfo: {
                        expiresAt: newExpiresAt,
                        refreshAt: newExpiresAt.getTime() - refreshBeforeMs,
                    },
                };
            },
            tokenValidator: async (token) => {
                try {
                    await this._client.call(me(), token);
                    return true;
                } catch {
                    return false;
                }
            },
            needSaveSession: (_, info) => {
                options?.onTokensRefreshed?.(info);
            },
            sessionClosed: () => {
                this._session = null;
                options?.onSessionClosed?.();
            },
        });

        this._session = session;
        this._sessionWasSet = true;
    }

    closeSession(): void {
        this._session = null;
        this._sessionWasSet = false;
    }

    async call<TResponse>(request: _RequestEnvelope<TResponse>): Promise<TResponse> {
        if (!this._session) {
            if (this._sessionWasSet) {
                throw new SessionExpiredError();
            }
            return this._client.call(request);
        }

        const authToken = await this._session.getAuthToken();
        if (authToken === null) {
            throw new SessionExpiredError();
        }

        return this._client.call(request, authToken);
    }
}
